import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import lebenslaufContentV2 from './lebenslaufContentV2';
import './Lebenslauf.css';

// Feedback-Runde: Java runtergeregelt (war 80, "mittelmäßige Erfahrung"),
// "AWS/Azure" -> nur Azure (kein AWS-Praxisbezug), "DevOps" aufgeteilt in
// CI/CD, Docker, Kubernetes statt einem vagen Sammelbalken.
const skillBarsV2 = [
  { name: 'JavaScript/TypeScript', level: 95 },
  { name: 'HTML5/CSS3', level: 90 },
  { name: 'PHP', level: 88 },
  { name: 'Python', level: 95 },
  { name: 'React/Vue/Angular', level: 90 },
  { name: 'TYPO3/WordPress', level: 95 },
  { name: 'Node.js', level: 85 },
  { name: 'SQL/NoSQL', level: 90 },
  { name: 'Java', level: 45 },
  { name: 'Git', level: 92 },
  { name: 'CI/CD-Pipelines', level: 70 },
  { name: 'Docker', level: 55 },
  { name: 'Kubernetes', level: 40 },
  { name: 'Microsoft Azure', level: 78 },
];

function LebenslaufV2() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentAddress, setCurrentAddress] = useState('berlin');
  const [cvLanguage, setCvLanguage] = useState(() => {
    return localStorage.getItem('lebenslauf-v2-language') || 'de';
  });
  const [showPhoto, setShowPhoto] = useState(() => {
    const stored = localStorage.getItem('lebenslauf-v2-show-photo');
    return stored === null ? true : stored === 'true';
  });
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingImage, setDownloadingImage] = useState(false);
  const cvRef = useRef(null);

  const c = lebenslaufContentV2[cvLanguage];
  const ui = c.ui;

  const switchCvLanguage = (lang) => {
    setCvLanguage(lang);
    localStorage.setItem('lebenslauf-v2-language', lang);
  };

  const togglePhoto = () => {
    setShowPhoto((prev) => {
      const next = !prev;
      localStorage.setItem('lebenslauf-v2-show-photo', String(next));
      return next;
    });
  };

  const addresses = {
    berlin: {
      display: 'Gartenfelderstraße 74b, 13599 Berlin',
    },
    nuremberg: {
      display: 'Westendstraße 28, 90427 Nürnberg',
    },
  };

  const fileSuffix = () => {
    const place = currentAddress === 'berlin' ? 'Berlin' : 'Nuremberg';
    return cvLanguage === 'en' ? `${place}_EN` : place;
  };

  const downloadPDF = async () => {
    setDownloadingPdf(true);
    try {
      const canvas = await html2canvas(cvRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 1200,
      });

      const { jsPDF: JsPDF } = window.jspdf || { jsPDF };
      const pdf = new JsPDF('p', 'mm', 'a4', true);

      // JPEG statt PNG hält die Datei versandfertig klein (PNG-Screenshots
      // dieser Größe landen sonst schnell bei mehreren MB).
      const imgData = canvas.toDataURL('image/jpeg', 0.8);
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
      heightLeft -= 295;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
        heightLeft -= 295;
      }

      pdf.save(`Emin_Tavli_Lebenslauf_${fileSuffix()}.pdf`);
    } catch (error) {
      console.error('PDF error:', error);
      alert(ui.errorPdf);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadImage = async () => {
    setDownloadingImage(true);
    try {
      const canvas = await html2canvas(cvRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 1200,
      });

      const link = document.createElement('a');
      link.download = `Emin_Tavli_Lebenslauf_${fileSuffix()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Image error:', error);
      alert(ui.errorImage);
    } finally {
      setDownloadingImage(false);
    }
  };

  return (
    <div className="lebenslauf-page">
      <button
        type="button"
        className="back-button"
        onClick={() => {
          const scrollTo = location.state && location.state.scrollTo ? location.state.scrollTo : 'contact';
          navigate('/', { state: { scrollTo } });
        }}
      >
        {ui.back}
      </button>

      <div className="lebenslauf-controls">
        <div className="address-selector">
          <button
            className={`address-btn ${currentAddress === 'berlin' ? 'active' : ''}`}
            onClick={() => setCurrentAddress('berlin')}
          >
            {ui.addressBerlin}
          </button>
          <button
            className={`address-btn ${currentAddress === 'nuremberg' ? 'active' : ''}`}
            onClick={() => setCurrentAddress('nuremberg')}
          >
            {ui.addressNuremberg}
          </button>
        </div>

        <div className="address-selector">
          <button
            className={`address-btn ${cvLanguage === 'de' ? 'active' : ''}`}
            onClick={() => switchCvLanguage('de')}
          >
            Deutsch
          </button>
          <button
            className={`address-btn ${cvLanguage === 'en' ? 'active' : ''}`}
            onClick={() => switchCvLanguage('en')}
          >
            English
          </button>
        </div>

        <div className="photo-toggle">
          <button
            className={`address-btn ${showPhoto ? 'active' : ''}`}
            onClick={togglePhoto}
          >
            {showPhoto ? ui.hidePhoto : ui.showPhoto}
          </button>
        </div>

        <div className="download-buttons">
          <button
            className="download-btn"
            onClick={downloadPDF}
            disabled={downloadingPdf}
          >
            {downloadingPdf ? ui.downloadingPdf : ui.downloadPdf}
          </button>
          <button
            className="download-btn"
            onClick={downloadImage}
            disabled={downloadingImage}
          >
            {downloadingImage ? ui.downloadingImage : ui.downloadImage}
          </button>
        </div>
      </div>

      <div className="cv-container" ref={cvRef}>
        <div className="cv-left">
          <div className="profile-section">
            {showPhoto && (
              <img
                src="/img/portrait.png"
                alt={c.name}
                className="profile-photo"
              />
            )}
            <div className="name">{c.name}</div>
            <div className="title">{c.title}</div>
          </div>

          <div className="summary">{c.summary}</div>

          <div className="cv-section">
            <div className="cv-section-title">{c.sections.contact}</div>
            <div className="contact-info">
              <p>{addresses[currentAddress].display}</p>
              <p>01727049258</p>
              <p>Tavlin@hotmail.de</p>
              <p>{c.birthDate}</p>
            </div>
          </div>

          <div className="cv-section">
            <div className="cv-section-title">{c.sections.skills}</div>
            <ul className="skills-list">
              {skillBarsV2.map((skill, idx) => (
                <li key={idx} className="skill-item">
                  <div className="skill-name">{skill.name}</div>
                  <div className="skill-bar">
                    <div
                      className="skill-progress"
                      style={{ width: `${skill.level}%` }}
                    ></div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="cv-section">
            <div className="cv-section-title">{c.sections.languages}</div>
            <div className="languages">
              {c.languages.map((lang, idx) => (
                <div className="language-item" key={idx}>
                  <span>{lang.name}</span>
                  <span>{lang.level}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="cv-right">
          <h2 className="main-title">{c.sections.experience}</h2>

          {c.experience.map((job, idx) => (
            <div className="experience-item" key={idx}>
              <div className="job-title">{job.role}</div>
              <div className="company">{job.company}</div>
              <div className="duration">{job.duration}</div>
              <ul className="responsibilities">
                {job.bullets.map((bullet, bIdx) => (
                  <li key={bIdx}>{bullet}</li>
                ))}
              </ul>
            </div>
          ))}

          <h3 className="section-header">{c.sections.projects}</h3>

          {c.projects.map((project, idx) => (
            <div className="education-item" key={idx}>
              <div className="degree-title">{project.name}</div>
              {project.tech && <div className="institution">{project.tech}</div>}
              <ul className="details">
                {project.bullets.map((bullet, bIdx) => (
                  <li key={bIdx}>{bullet}</li>
                ))}
              </ul>
            </div>
          ))}

          <h3 className="section-header">{c.sections.education}</h3>

          {c.education.map((edu, idx) => (
            <div className="education-item" key={idx}>
              <div className="degree-title">{edu.degree}</div>
              <div className="institution">{edu.institution}</div>
              <div className="duration">{edu.duration}</div>
              <ul className="details">
                {edu.bullets.map((bullet, bIdx) => (
                  <li key={bIdx}>{bullet}</li>
                ))}
              </ul>
            </div>
          ))}

          <div className="additional-info">
            <h3 className="section-header">{c.sections.additional}</h3>

            <div className="additional-section">
              <h4>{c.sections.schoolEducation}</h4>
              {c.additional.schoolEducation.map((line, idx) => (
                <p key={idx}>{line}</p>
              ))}
            </div>

            <div className="additional-section">
              <h4>{c.sections.earlyExperience}</h4>
              {c.additional.earlyExperience.map((line, idx) => (
                <p key={idx}>{line}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LebenslaufV2;
